import { useMutation } from "@apollo/client";
import { useEffect, useState, type ReactNode } from "react";
import { APPROVE_CHANGE_REQUEST_MUTATION, ChangeRequestStatus, Node } from "../queries";
import Button from "@/components/Button";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import Progress from "@/components/Progress";
import TextField from "@/components/TextField";
import useAmplitude from "@/hooks/useAmplitude";
import useModal from "@/hooks/useModal";
import { SECTION_ANALYTICS_EVENTS, getSectionAnalyticsData } from "@/pages/sections/_utils/amplitude";
import { useChangeRequestStatusChangeEmailPreview } from "../hooks/useChangeRequestStatusChangeEmailPreview";

const employmentApproveCopy =
  "Are you sure you wish to approve this change request? The member information will be updated with the requested changes, and the member will receive notification of the update. You may include a message to the member as well.";

const nonActiveApprovalFollowUpCopy =
  "Approval creates a Membership Services follow-up task and does not immediately update the member's classification.";

function nonActiveClassificationApproveCopy(requestedClassificationLabel: string): ReactNode {
  return (
    <>
      {`Are you sure you wish to approve this ${requestedClassificationLabel} Classification request? `}
      <strong>{nonActiveApprovalFollowUpCopy}</strong>
      {" You may include an optional message for the task."}
    </>
  );
}

export default function ApproveModal({
  changeRequest: { id: changeRequestId, change, section },
  onClosed
}: {
  changeRequest: Node;
  onClosed(): void;
}) {
  const { save, Modal, close } = useModal();
  const { trackEvent } = useAmplitude();
  const [message, setMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useChangeRequestStatusChangeEmailPreview(changeRequestId, ChangeRequestStatus.APPROVED);

  useEffect(() => () => resetPreview(), [resetPreview]);

  const isNonActiveClassificationRequest = change.__typename === "BackofficeLifeMemberChange";
  const submitLabel = isNonActiveClassificationRequest ? "Approve" : message ? "Approve and Send" : "Approve";
  const approveCopy = isNonActiveClassificationRequest
    ? nonActiveClassificationApproveCopy(change.requestedClassificationLabel)
    : employmentApproveCopy;
  const messageLabel = isNonActiveClassificationRequest
    ? "Message for Task (Optional)"
    : "Message to Member (Optional)";

  const [approveChangeRequest, { loading }] = useMutation(APPROVE_CHANGE_REQUEST_MUTATION);

  const submit = () =>
    save(
      async () => {
        const result = await approveChangeRequest({ variables: { id: changeRequestId, message } });
        if (result.data?.approveChangeRequest?.success) {
          trackEvent(
            SECTION_ANALYTICS_EVENTS.changeRequestApproved,
            getSectionAnalyticsData({
              source: "section_change_requests",
              sectionId: section.id,
              changeRequestId,
              changeType: change.__typename,
              action: "approve",
              hasMessage: Boolean(message.trim()),
              createsFollowUpTask: isNonActiveClassificationRequest
            })
          );
        }
        return result;
      },
      (data) => data.approveChangeRequest,
      "Change request approved"
    );

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview(message);
  };

  return (
    <>
      <Modal
        onClosed={onClosed}
        className={EMAIL_PREVIEW_MODAL_CLASSNAME}
        actions={
          <EmailPreviewModalActions
            onPreview={handlePreviewEmail}
            previewDisabled={loading}
            showPreview={!isNonActiveClassificationRequest}
          >
            <Button variant="text" onClick={close} disabled={loading}>
              Cancel
            </Button>
            <Button disabled={loading} onClick={submit} icon="check">
              {submitLabel}
            </Button>
          </EmailPreviewModalActions>
        }
        headline="Approve Change Request"
      >
        {loading ? (
          <Progress variant="linear" />
        ) : (
          <>
            <p>{approveCopy}</p>

            <TextField
              className="w-full h-48 mt-6 mb-3"
              variant="outlined"
              type="textarea"
              label={messageLabel}
              value={message}
              onChangeText={setMessage}
            />
          </>
        )}
      </Modal>
      {!isNonActiveClassificationRequest && (
        <EmailPreviewDialog
          open={previewOpen}
          onClosed={() => setPreviewOpen(false)}
          subject={previewLoading ? undefined : preview?.subject}
          html={previewLoading ? undefined : preview?.html}
          loading={previewLoading}
          errorMessage={previewLoading ? null : (previewError?.message ?? null)}
        />
      )}
    </>
  );
}
